import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for handling pagination logic with RTK Query
 * Caller controls the offset, hook handles data accumulation and loading state
 *
 * @param queryResult - The RTK Query result object
 * @param limit - The page/limit size
 * @param offset - Current offset (controlled by caller)
 * @param setOffset - Function to update offset (controlled by caller)
 * @param getItemId - Optional function to extract unique ID from items for deduplication
 */
export const usePagination = <T>(
  queryResult: {
    data?: T[];
    originalArgs?: { offset: number };
    isFetching: boolean;
  },
  limit: number,
  offset: number,
  setOffset: React.Dispatch<React.SetStateAction<number>>,
  getItemId?: (item: T) => string | number,
) => {
  const [allData, setAllData] = useState<T[]>([]);
  const [endReached, setEndReached] = useState(false);
  // Track last processed offset to prevent duplicate processing
  const lastProcessedOffsetRef = useRef<number | null>(null);

  const { data, originalArgs, isFetching } = queryResult;

  // Handle pagination logic - accumulate data and update offset
  useEffect(() => {
    if (
      data &&
      originalArgs &&
      !isFetching &&
      originalArgs.offset !== lastProcessedOffsetRef.current
    ) {
      // Mark this offset as processed
      lastProcessedOffsetRef.current = originalArgs.offset;

      // Append new data, avoiding duplicates if getItemId is provided
      setAllData((prev) => {
        if (getItemId) {
          const existingIds = new Set(prev.map(getItemId));
          const newItems = data.filter(
            (item) => !existingIds.has(getItemId(item)),
          );
          return prev.concat(newItems);
        }
        return prev.concat(data);
      });

      // Update offset or mark end reached
      if (data.length === limit) {
        // More data available, update offset for next query
        setOffset(originalArgs.offset + limit);
      } else {
        // End of data reached
        setEndReached(true);
      }
    }
  }, [data, originalArgs, isFetching, limit, setOffset, getItemId]);

  // Determine loading state - prevent flickering between pagination cycles
  // Keep loading if currently fetching OR we've started fetching but haven't reached the end yet
  const hasStartedFetching = allData.length > 0 || isFetching;
  const fetching = isFetching || (hasStartedFetching && !endReached);

  const resetPagination = () => {
    setOffset(0);
    setAllData([]);
    setEndReached(false);
    lastProcessedOffsetRef.current = null;
  };

  return {
    allData,
    endReached,
    isFetching,
    fetching, // Stable loading state that prevents flickering
    resetPagination,
  };
};
